import { Edit, Save } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import Linkify from "linkify-react";
import { useState } from "react";

import { CharacterLimitedTextField as TextField } from "~ui/atoms/CharacterLimitedTextField/CharacterLimitedTextField";

interface LinkifiedEditableFieldProps {
  label?: string;
  maxLength?: number;
  value?: string | null;
  onUpdate: (value: string) => Promise<void>;
}

export const LinkifiedEditableField = ({ label, maxLength, value, onUpdate }: LinkifiedEditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value ?? "");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async () => {
    if (isUpdating) {
      return;
    }

    setIsUpdating(true);
    try {
      await onUpdate(tempValue);
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {label && (
        <Typography component="span" sx={{ fontWeight: "bold" }}>
          {label}:
        </Typography>
      )}
      {isEditing ? (
        <>
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            size="small"
            disabled={isUpdating}
            inputProps={maxLength === undefined ? undefined : { maxLength }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSave();
              }
            }}
          />
          <IconButton onClick={handleSave} size="small" disabled={isUpdating} aria-label="Save field">
            <Save />
          </IconButton>
        </>
      ) : (
        <>
          <Typography component="span">
            {value ? (
              <Linkify
                options={{
                  rel: "noopener noreferrer",
                  style: { wordBreak: "break-word" },
                  target: "_blank",
                }}
              >
                {value}
              </Linkify>
            ) : (
              "Not set"
            )}
          </Typography>
          <IconButton onClick={() => setIsEditing(true)} size="small" aria-label="Edit field">
            <Edit />
          </IconButton>
        </>
      )}
    </Box>
  );
};
